import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
      grey: {
        100: "#f5f6fa",
        200: "#dcdde1",
        300: "#a4b0be",
        400: "#747d8c",
        500: "#57606f",
        600: "#2f3542",
        700: "#2c3e50",
        800: "#1e272e",
        900: "#0a0a0a"
      },
      primary: {
        100: "#dfe6e9",
        200: "#b2bec3",
        300: "#636e72",
        400: "#2d3436",
        500: "#1e272e",
        600: "#1a2228",
        700: "#141a1f",
        800: "#0f1419",
        900: "#0a0d11"
      },
      blueAccent: {
        100: "#a0c4ff",
        200: "#89b4ff",
        300: "#74a9ff",
        400: "#5c9eff",
        500: "#4a90e2",
        600: "#3f7ecb",
        700: "#356db4",
        800: "#2a5b9c",
        900: "#204a85"
      }
    }
    : {
      grey: {
        100: "#0a0a0a",
        200: "#1e272e",
        300: "#2c3e50",
        400: "#57606f",
        500: "#747d8c",
        600: "#a4b0be",
        700: "#dcdde1",
        800: "#f5f6fa",
        900: "#ffffff"
      },
      primary: {
        100: "#f5f6fa",
        200: "#dcdde1",
        300: "#b2bec3",
        400: "#a4b0be",
        500: "#4a90e2",
        600: "#357ABD",
        700: "#2c3e50",
        800: "#1e272e",
        900: "#0a0a0a"
      },
      blueAccent: {
        100: "#eaf2ff",
        200: "#c6dfff",
        300: "#a0c4ff",
        400: "#74a9ff",
        500: "#4a90e2",
        600: "#357ABD",
        700: "#2a5b9c",
        800: "#204a85",
        900: "#183a66"
      },
      white: {
        100: "#ffffff",
        200: "#f9f9f9",
        300: "#f2f2f2",
        400: "#ebebeb",
        500: "#e3e3e3",
        600: "#dcdcdc",
        700: "#d5d5d5",
        800: "#cecece",
        900: "#c6c6c6"
      },
    })
});

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode,
      primary: { main: colors.primary[500] },
      secondary: { main: colors.blueAccent[500] },
      neutral: {
        dark: colors.grey[700],
        main: colors.grey[500],
        light: colors.grey[100]
      },
      background: {
        default: mode === "dark" ? colors.primary[500] : "#f7f9fc"
      }
    },
    typography: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 13,
      h1: { fontSize: 40, fontWeight: 600 },
      h2: { fontSize: 32, fontWeight: 600 },
      h3: { fontSize: 24, fontWeight: 500 },
      h4: { fontSize: 20, fontWeight: 500 },
      h5: { fontSize: 16, fontWeight: 500 },
      h6: { fontSize: 14, fontWeight: 500 }
    }
  };
};

export const ColorModeContext = createContext({ toggleColorMode: () => { } });

export const useMode = () => {
  const [mode, setMode] = useState("light");
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "dark" ? "light" : "dark"))
    }),
    []
  );
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
